import { useEffect, useRef, useState, useMemo } from 'react';
import Highcharts, {
    AxisLabelsFormatterContextObject,
    dateFormat,
    Options,
    SeriesOptionsType,
} from 'highcharts/highstock';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Box, Flex } from '@chakra-ui/react';
import { DateInterval, StandardReportInfo } from 'types/reporting';
import { useGetInfoItemsSeriesOptions, useGetQuickPickSeries } from 'query/reporting';
import { dateIntervalOptions, getSharedChartOptions } from 'components/reporting/utils';
import { DateIntervalSelect } from 'components/reporting/DateIntervalSelect';
import { merge } from 'lodash';
import { colors } from 'theme/colors';

const chartOptions = (intl: IntlShape, dateInterval?: DateInterval): Options => {
    const dateFormatStr = dateInterval === 'Yr' ? '%Y' : '%Y-%m';
    return merge({}, getSharedChartOptions(intl), {
        exporting: {
            csv: {
                dateFormat: dateFormatStr,
            },
        },
        title: {
            text: intl.formatMessage({ id: 'reporting_portfolio_energy_revenue_portfolio_net_revenue_title' }),
        },
        yAxis: {
            title: {
                text: '$',
            },
            plotLines: [
                {
                    value: 0,
                    color: 'var(--chakra-colors-text-secondary)',
                    width: 1,
                    zIndex: 5,
                },
            ],
        },
        xAxis: {
            type: 'datetime',
            labels: {
                formatter: (context: AxisLabelsFormatterContextObject) => {
                    return dateFormat(dateFormatStr, Number(context.value));
                },
            },
        },
        rangeSelector: {
            enabled: true,
            inputEnabled: false,
            buttons: [{ type: 'all', text: 'All', title: 'View all' }],
            selected: 4,
        },
        legend: {
            enabled: true,
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemMarginTop: 10,
            itemMarginBottom: 10,
            padding: 40,
        },
        tooltip: {
            shared: false,
            split: false,
            formatter: function () {
                if (!!this.y) {
                    const formattedDate = dateFormat(dateFormatStr, Number(this.x));
                    const value = Math.round(this.y).toLocaleString('en-US');
                    return `<b>${formattedDate}</b><br/><span style="color:${this.color}">${this.series.name}</span>: <b>$${value}</b>`;
                }
                return '';
            },
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                dataLabels: {
                    enabled: false,
                },
            },
        },
    } as Options);
};

interface PortfolioNetRevenueProps {
    info: StandardReportInfo;
}

export function PortfolioNetRevenue({ info }: PortfolioNetRevenueProps) {
    const intl = useIntl();
    const ref = useRef<HighchartsReactRefObject>(null);
    const [selectedDateInterval, setSelectedDateInterval] = useState<DateInterval>(dateIntervalOptions[0].value);

    const options = useMemo(() => {
        return chartOptions(intl, selectedDateInterval);
    }, [intl, selectedDateInterval]);

    const {
        data: seriesOptions,
        isFetching,
        isError,
    } = useGetInfoItemsSeriesOptions({
        info,
        reportType: 'PortfolioEnergyAndRev',
        quickPickSeries: selectedDateInterval === 'Yr' ? 'YrItemNetRevDol' : 'MoItemNetRevDol',
    });

    const { data: totalData, isFetching: isFetchingTotal } = useGetQuickPickSeries({
        info,
        reportType: 'PortfolioEnergyAndRev',
        quickPickSeries: selectedDateInterval === 'Yr' ? 'YrPortNetRevDol' : 'MoPortNetRevDol',
    });

    useEffect(() => {
        if (ref.current) {
            isFetching || isFetchingTotal ? ref.current.chart.showLoading() : ref.current.chart.hideLoading();
        }
    }, [ref, isFetching, isFetchingTotal]);

    useEffect(() => {
        if (ref.current && seriesOptions) {
            const chart = ref.current.chart;
            chart.hideData();

            while (chart.series.length > 0) {
                chart.series[0].remove(false);
            }

            seriesOptions?.forEach((s) => {
                chart.addSeries(s, false);
            });

            if (totalData && totalData.length > 0) {
                const totalDataSeries: SeriesOptionsType = {
                    type: 'line',
                    name: intl.formatMessage({ id: 'reporting_portfolio_energy_revenue_portfolio_net_revenue_title' }),
                    data: totalData,
                    color: colors.red[900],
                    lineWidth: 3,
                    dashStyle: 'Dash',
                    zIndex: 10,
                    legendIndex: 0,
                };
                chart.addSeries(totalDataSeries, false);
            }

            const intervalLabel =
                selectedDateInterval === 'Yr'
                    ? intl.formatMessage({ id: 'common_annual' })
                    : intl.formatMessage({ id: 'common_monthly' });
            const title = `${intervalLabel} ${intl.formatMessage({ id: 'reporting_portfolio_energy_revenue_portfolio_net_revenue_title' })}`;

            chart.setTitle({ text: title });

            chart.redraw();
        }
    }, [ref, info, seriesOptions, totalData, intl, selectedDateInterval]);

    if (isError) {
        return <FormattedMessage id="common_generic_loading_error" />;
    }

    return (
        <Box h="full" w="full">
            <Flex p={4}>
                <DateIntervalSelect value={selectedDateInterval} onChange={setSelectedDateInterval} />
            </Flex>
            <HighchartsReact
                ref={ref}
                options={options}
                highcharts={Highcharts}
                containerProps={{ style: { width: '100%', height: '60vh', minHeight: '400px' } }}
                constructorType={'stockChart'}
            />
        </Box>
    );
}
